import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import LandingPage from './components/landing/LandingPage';
import { STOCKTAKE_ROLES } from './constants/stocktakeRoles';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SystemAdminRoute from './components/auth/SystemAdminRoute';
import PermissionRoute from './components/auth/PermissionRoute';
import UserList from './components/users/UserList';
import InstitutionList from './components/institutions/InstitutionList';
import InstitutionDetail from './components/institutions/InstitutionDetail';
import CreateInstitution from './components/institutions/CreateInstitution';
import InstitutionSettingsPage from './components/institutions/InstitutionSettingsPage';
import TenantProvisioning from './components/institutions/TenantProvisioning';
import Sidebar from './components/shared/Sidebar';
import { DashboardAccessProvider } from './components/dashboard/useDashboardAccess';
import MainHeader from './components/shared/MainHeader';
import { AuthService } from './services/auth.service';
import StoreList from './components/store/StoreList';
import ItemCategoryList from './components/item/ItemCategoryList';
import ItemList from './components/item/ItemList';
import StockIn from './components/item/StockIn';
import StockOut from './components/item/StockOut';
import './App.css';
import UserCreate from './components/users/UserCreate';
import AllInstitutionUsers from './components/users/AllInstitutionUsers';
import StockList from './components/item/StockList';
import StockHistory from './components/item/StockHistory';
import LowStockList from './components/item/LowStockList';
import StocktakeList from './components/stocktake/StocktakeList';
import StocktakeCreate from './components/stocktake/StocktakeCreate';
import StocktakeDetails from './components/stocktake/StocktakeDetails';
import StockTransferList from './components/transfer/StockTransferList';
import StockTransferCreate from './components/transfer/StockTransferCreate';
import StockTransferDetails from './components/transfer/StockTransferDetails';
import UnitOfMeasurementList from './components/item/UnitOfMeasurementList';
import AllInstitutionStoreList from "./components/store/AllInstitutionStoreList";
import UserProfile from './components/users/UserProfile';
import ModulesPage from './components/modules/ModulesPage';
import { PERMISSIONS } from './constants/permissions';
import { UserType } from './interfaces/userType.enum';
import ChartOfAccounts from './components/accounting/ChartOfAccounts';
import JournalEntryList from './components/accounting/JournalEntryList';
import JournalEntryCreate from './components/accounting/JournalEntryCreate';
import JournalEntryDetail from './components/accounting/JournalEntryDetail';
import TransactionList from './components/accounting/TransactionList';
import AccountLedger from './components/accounting/AccountLedger';
import TrialBalance from './components/accounting/TrialBalance';
import LedgerCloseSettings from './components/accounting/LedgerCloseSettings';
import SegmentTrialBalance from './components/accounting/SegmentTrialBalance';
import SegmentIncomeStatement from './components/accounting/SegmentIncomeStatement';
import SegmentBalanceSheet from './components/accounting/SegmentBalanceSheet';
import Dashboard from './components/dashboard/Dashboard';
import ModuleBoard from './components/dashboard/board/ModuleBoard';
import KpiTargetsSettings from './components/dashboard/pages/KpiTargetsSettings';
import { MODULE_DEFAULT_PRESETS } from './components/dashboard/registry/presets';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import UserPermissions from './components/users/UserPermissions';
import { SupplierList } from './components/procurement/SupplierList';
import { SupplierForm } from './components/procurement/SupplierForm';
import { SupplierDetail } from './components/procurement/SupplierDetail';
import { SupplierCatalogueBrowse } from './components/procurement/SupplierCatalogueBrowse';
import { ProcurementSettings } from './components/procurement/ProcurementSettings';
import { PurchaseOrderList } from './components/procurement/PurchaseOrderList';
import { PurchaseOrderCreate } from './components/procurement/PurchaseOrderCreate';
import { PendingApprovalPurchaseOrders } from './components/procurement/PendingApprovalPurchaseOrders';
import { PendingApprovalPurchaseOrderDetail } from './components/procurement/PendingApprovalPurchaseOrderDetail';
import { PurchaseOrderDetail } from './components/procurement/PurchaseOrderDetail';
import { GoodsReceiptList } from './components/procurement/GoodsReceiptList';
import { GoodsReceiptCreate } from './components/procurement/GoodsReceiptCreate';
import { GoodsReceiptDetail } from './components/procurement/GoodsReceiptDetail';
import { SupplierBillList } from './components/procurement/SupplierBillList';
import { SupplierBillCreate } from './components/procurement/SupplierBillCreate';
import { SupplierBillDetail } from './components/procurement/SupplierBillDetail';
import { SupplierBillEdit } from './components/procurement/SupplierBillEdit';
import { SupplierPaymentList } from './components/procurement/SupplierPaymentList';
import { SupplierPaymentCreate } from './components/procurement/SupplierPaymentCreate';
import { SupplierPaymentDetail } from './components/procurement/SupplierPaymentDetail';
import { SupplierPaymentEdit } from './components/procurement/SupplierPaymentEdit';
import CustomerList from './components/sales/CustomerList';
import CustomerForm from './components/sales/CustomerForm';
import CustomerDetail from './components/sales/CustomerDetail';
import SalesSettings from './components/sales/SalesSettings';
import InvoiceBrandingSettings from './components/sales/InvoiceBrandingSettings';
import SalesInvoiceList from './components/sales/SalesInvoiceList';
import SalesInvoiceCreate from './components/sales/SalesInvoiceCreate';
import SalesInvoiceDetail from './components/sales/SalesInvoiceDetail';
import SalesInvoiceEdit from './components/sales/SalesInvoiceEdit';
import ReceiptDetail from './components/sales/ReceiptDetail';
import QuotationList from './components/sales/QuotationList';
import QuotationCreate from './components/sales/QuotationCreate';
import QuotationDetail from './components/sales/QuotationDetail';
import ArAgingReport from './components/sales/ArAgingReport';
import CustomerStatement from './components/sales/CustomerStatement';
import PosSettingsScreen from './components/pos/PosSettingsScreen';
import PosTerminalList from './components/pos/PosTerminalList';
import PosCashierAccess from './components/pos/PosCashierAccess';
import PosSaleList from './components/pos/PosSaleList';
import PosSaleDetail from './components/pos/PosSaleDetail';
import PosUnpostedQueue from './components/pos/PosUnpostedQueue';
import PosDayEndReport from './components/pos/PosDayEndReport';
import BillingPage from './components/billing/BillingPage';
import OperatorBillingPage from './components/billing/OperatorBillingPage';

// Component to handle login route logic
const LoginRoute = () => {
  const location = useLocation();
  const from = location.state?.from || '/';

  if (AuthService.isValidToken()) {
    return <Navigate to={from} replace />;
  }

  return <LandingPage />;
};

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <AuthProvider>
      <ToastProvider>
      <Router>
        <div className="app">
          <MainHeader />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />

            {/* Protected Routes with Sidebar */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/*"
                element={
                  <DashboardAccessProvider>
                  <div className="app-container">
                    <ImpersonationBanner />
                    <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(prev => !prev)} />
                    <div
                      className="content-container"
                      style={{
                        marginLeft: sidebarCollapsed ? '60px' : '250px',
                        width: sidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 250px)',
                      }}
                    >
                      <main className="main-content">
                        <Routes>
                          {/* Institution Admin Only Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_USERS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/users" element={<UserList />} />
                            <Route path="/users/create" element={<UserCreate />} />
                            <Route path="/users/:userId/permissions" element={<UserPermissions />} />
                            <Route path="/stores" element={<StoreList />} />
                          </Route>

                          {/* Billing — InstitutionAdmin only */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/billing" element={<BillingPage />} />
                          </Route>

                          {/* Operator Billing console — SaaSOperator + SystemAdmin */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.SystemAdmin, UserType.SaaSOperator]} />}>
                            <Route path="/operator/billing" element={<OperatorBillingPage />} />
                          </Route>

                          {/* System Admin Only Routes */}
                          <Route element={<SystemAdminRoute />}>
                            <Route path="/institutions" element={<InstitutionList />} />
                            <Route path="/institutions/create" element={<CreateInstitution />} />
                            <Route path="/institutions/:id" element={<InstitutionDetail />} />
                            <Route path="/all-institution-users" element={<AllInstitutionUsers />} />
                            <Route path="/app-institution-stores" element={<AllInstitutionStoreList />} />
                          </Route>

                          {/* SaaS Operator console — premium dashboard provisioning across institutions */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.SaaSOperator, UserType.SystemAdmin]} />}>
                            <Route path="/saas/premium-provisioning" element={<TenantProvisioning />} />
                          </Route>

                          {/* Institution Admin can manage their own institution's settings (e.g. two-factor policy) */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.SystemAdmin]} />}>
                            <Route path="/institution-settings" element={<InstitutionSettingsPage />} />
                          </Route>

                          {/* Store Admin and Institution Admin Only Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_STORES} allowedRoles={[UserType.InstitutionAdmin, UserType.StoreAdmin]} />}>
                            <Route path="/item-categories" element={<ItemCategoryList />} />
                            <Route path="/items" element={<ItemList />} />
                            <Route path="/stock-in" element={<StockIn />} />
                            <Route path="/stock-out" element={<StockOut />} />
                            <Route path="/stocks" element={<StockList />} />
                            <Route path="/low-stock" element={<LowStockList />} />
                            <Route path="/stock-history" element={<StockHistory />} />
                            <Route path="/unit-of-measurement" element={<UnitOfMeasurementList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCKTAKE_VIEW} allowedRoles={STOCKTAKE_ROLES} />}>
                            <Route path="/stocktakes" element={<StocktakeList />} />
                            <Route path="/stocktakes/create" element={<StocktakeCreate />} />
                            <Route path="/stocktakes/:id" element={<StocktakeDetails />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_TRANSFER_VIEW} allowedRoles={[UserType.InstitutionAdmin, UserType.StoreAdmin]} />}>
                            <Route path="/stock-transfers" element={<StockTransferList />} />
                            <Route path="/stock-transfers/create" element={<StockTransferCreate />} />
                            <Route path="/stock-transfers/:id" element={<StockTransferDetails />} />
                          </Route>

                          {/* User Profile Route - Available for all authenticated users */}
                          <Route path="/profile" element={<UserProfile />} />

                          {/* Accounting Routes - Accountant and Institution Admin */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_ACCOUNTING} allowedRoles={[UserType.Accountant, UserType.InstitutionAdmin]} />}>
                            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
                            <Route path="/accounting/transactions" element={<TransactionList />} />
                            <Route path="/accounting/journal-entries" element={<JournalEntryList />} />
                            <Route path="/accounting/journal-entries/create" element={<JournalEntryCreate />} />
                            <Route path="/accounting/journal-entries/:id" element={<JournalEntryDetail />} />
                            <Route path="/accounting/accounts/:id/ledger" element={<AccountLedger />} />
                            <Route path="/accounting/trial-balance" element={<TrialBalance />} />
                            <Route path="/accounting/ledger-close-settings" element={<LedgerCloseSettings />} />
                            <Route path="/accounting/segment-trial-balance" element={<SegmentTrialBalance />} />
                            <Route path="/accounting/income-statement" element={<SegmentIncomeStatement />} />
                            <Route path="/accounting/balance-sheet" element={<SegmentBalanceSheet />} />
                          </Route>

                          {/* Procurement Routes */}
                          <Route path="/procurement/suppliers" element={<SupplierList />} />
                          <Route path="/procurement/suppliers/new" element={<SupplierForm />} />
                          <Route path="/procurement/suppliers/:id" element={<SupplierDetail />} />
                          <Route path="/procurement/suppliers/:id/edit" element={<SupplierForm />} />
                          <Route path="/procurement/catalogue" element={<SupplierCatalogueBrowse />} />
                          <Route path="/procurement/settings" element={<ProcurementSettings />} />
                          <Route path="/procurement/purchase-orders" element={<PurchaseOrderList />} />
                          <Route path="/procurement/purchase-orders/new" element={<PurchaseOrderCreate />} />
                          <Route path="/procurement/purchase-orders/pending-approval" element={<PendingApprovalPurchaseOrders />} />
                          <Route path="/procurement/purchase-orders/pending-approval/:id" element={<PendingApprovalPurchaseOrderDetail />} />
                          <Route path="/procurement/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                          <Route path="/procurement/goods-receipts" element={<GoodsReceiptList />} />
                          <Route path="/procurement/goods-receipts/new" element={<GoodsReceiptCreate />} />
                          <Route path="/procurement/goods-receipts/:id" element={<GoodsReceiptDetail />} />
                          <Route path="/procurement/supplier-bills" element={<SupplierBillList />} />
                          <Route path="/procurement/supplier-bills/new" element={<SupplierBillCreate />} />
                          <Route path="/procurement/supplier-bills/:id" element={<SupplierBillDetail />} />
                          <Route path="/procurement/supplier-bills/:id/edit" element={<SupplierBillEdit />} />
                          <Route path="/procurement/supplier-payments" element={<SupplierPaymentList />} />
                          <Route path="/procurement/supplier-payments/new" element={<SupplierPaymentCreate />} />
                          <Route path="/procurement/supplier-payments/:id" element={<SupplierPaymentDetail />} />
                          <Route path="/procurement/supplier-payments/:id/edit" element={<SupplierPaymentEdit />} />

                          {/* Sales Routes */}
                          <Route path="/sales/customers" element={<CustomerList />} />
                          <Route path="/sales/customers/new" element={<CustomerForm />} />
                          <Route path="/sales/customers/:id" element={<CustomerDetail />} />
                          <Route path="/sales/customers/:id/edit" element={<CustomerForm />} />
                          <Route path="/sales/settings" element={<SalesSettings />} />
                          <Route path="/sales/branding" element={<InvoiceBrandingSettings />} />
                          <Route path="/sales/invoices" element={<SalesInvoiceList />} />
                          <Route path="/sales/invoices/new" element={<SalesInvoiceCreate />} />
                          <Route path="/sales/invoices/:id" element={<SalesInvoiceDetail />} />
                          <Route path="/sales/invoices/:id/edit" element={<SalesInvoiceEdit />} />
                          <Route path="/sales/receipts/:id" element={<ReceiptDetail />} />
                          <Route path="/sales/quotations" element={<QuotationList />} />
                          <Route path="/sales/quotations/new" element={<QuotationCreate />} />
                          <Route path="/sales/quotations/:id" element={<QuotationDetail />} />
                          <Route path="/sales/quotations/:id/edit" element={<QuotationCreate />} />
                          <Route path="/sales/reports/aging" element={<ArAgingReport />} />
                          <Route path="/sales/reports/statement" element={<CustomerStatement />} />

                          {/* POS Routes — Task 10 adds its routes below this line */}
                          <Route path="/pos/settings" element={<PosSettingsScreen />} />
                          <Route path="/pos/terminals" element={<PosTerminalList />} />
                          <Route path="/pos/cashiers" element={<PosCashierAccess />} />
                          <Route path="/pos/sales" element={<PosSaleList />} />
                          <Route path="/pos/sales/:id" element={<PosSaleDetail />} />
                          <Route path="/pos/unposted" element={<PosUnpostedQueue />} />
                          <Route path="/pos/day-end" element={<PosDayEndReport />} />

                          {/* Dashboard module deep-pages — reached from the Dashboard submenu */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant]} />}>
                            <Route path="/dashboard/accounting" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="accounting" moduleScope="accounting" fallbackIds={MODULE_DEFAULT_PRESETS.accounting} title="Accounting" subtitle="Receivables, spend and monthly income vs expense." />} />
                            <Route path="/dashboard/inventory" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="inventory" moduleScope="inventory" fallbackIds={MODULE_DEFAULT_PRESETS.inventory} title="Inventory" subtitle="Stock value, movement and reorder alerts." />} />
                            <Route path="/dashboard/sales" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="sales" moduleScope="sales" fallbackIds={MODULE_DEFAULT_PRESETS.sales} title="Sales" subtitle="Pipeline, revenue and quotation outcomes." />} />
                          </Route>

                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.InstitutionManager]} />}>
                            <Route path="/dashboard/settings/targets" element={<KpiTargetsSettings />} />
                          </Route>

                          <Route path="/modules" element={<ModulesPage />} />
                          <Route path="/" element={<Dashboard />} />
                        </Routes>
                      </main>
                    </div>
                  </div>
                  </DashboardAccessProvider>
                }
              />
            </Route>
          </Routes>
        </div>
      </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
